import React from 'react';
import Box from '@components/Atoms/Box';
import Paper from '@components/Atoms/Paper';
import Typography from '@components/Atoms/Typography';
import SplitButton from '@components/Atoms/SplitButton';
import ConfirmDialog from '@components/Atoms/ConfirmDialog';
import Button from '@components/Atoms/Button';
import ShowIf from '@components/Atoms/ShowIf';
import { useSessionManagement } from '../contexts/SessionManagementProvider';

type Props = {
  children: React.ReactNode;
};

const SessionManagementToolbar = ({ children }: Props) => {
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const { 
    selectedStudents,
    handleResetTestletSession,
    setIsExtendTimeDialogOpen,
  } = useSessionManagement();
  return (
    <>
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => {
          handleResetTestletSession();
          setShowConfirmDialog(false);
        }}
        title="Confirm Reset Progress"
        message={
          <>
            <Typography gutterBottom color="error" fontWeight="bold">
              Resetting will throw away all answers and progress for the selected
              sessions!
            </Typography>
            <Typography gutterBottom>
              If you want the student to continue from where they left off, please use the
              &lsquo;Extend Time&rsquo; option instead.
            </Typography>
            <Typography fontWeight="bold">Are you sure you want to proceed?</Typography>
          </>
        }
      />
      <Paper
        elevation={1}
        sx={{ p: 3, borderRadius: 2, backgroundColor: 'background.default', mt: 4 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" component="p" color="text.secondary">
              Session Management
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedStudents.length} student
              {selectedStudents.length !== 1 ? 's' : ''} selected
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <SplitButton
                options={[
                  {
                    id: 'extend_time',
                    label: 'Extend Time',
                    onClick: () => setIsExtendTimeDialogOpen(true),
                    disabled: selectedStudents.length === 0,
                  },
                  {
                    id: 'reset_progress',
                    label: 'Reset Progress',
                    onClick: () => setShowConfirmDialog(true),
                    disabled: selectedStudents.length === 0,
                  },
                ]}
                defaultOptionId="extend_time"
              />
            </Box>
          </Box>
        </Box>
        {children}
      </Paper>
    </>
  );
};

export default SessionManagementToolbar;
